import {Controller} from '@hotwired/stimulus'
import {German} from 'flatpickr/dist/l10n/de'

export default class extends Controller {
  static targets = ['startTime', 'startDate', 'endDate', 'endTime', 'currentTime', 'overrideToggle']
  static values = {
    format: String,
    enableTime: Boolean,
    noCalendar: Boolean,
    disabledDays: Array,
    minDate: String,
    overrideMinDate: String,
    adminCanOverride: Boolean,
    overrideActive: Boolean,
    externalToggleId: String,
    onlyEnd: Boolean,
    editMode: Boolean
  }

  async connect() {
    if (this.externalToggleIdValue) {
      this.externalToggle = document.getElementById(this.externalToggleIdValue)
      if (this.externalToggle) {
        this._onExternalToggle = () => this.toggleOverride()
        this.externalToggle.addEventListener('change', this._onExternalToggle)
      }
    }

    // Get type_ids from hidden fields
    const typeIdElement = document.getElementById('booking_type_ids')
    const typeIdInputs = document.querySelectorAll('input[name="booking[type_ids][]"]')

    if (typeIdElement) {
      this.type_ids = $(typeIdElement).val()
      typeIdElement.addEventListener('change', this.newTypeIds)
    } else if (typeIdInputs.length > 0) {
      // Get first type_id from hidden fields
      this.type_ids = typeIdInputs[0].value
    } else {
      this.type_ids = ""
    }

    const siteRadios = document.querySelectorAll('input[type="radio"][name*="site_id"]')
    siteRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (this.type_ids != "" && this.hasStartDateTarget) {
          this.siteChanged = true
          this.refreshForSiteChange()
        }
      })
    })

    // Listen for collapse shown event to reinitialize if needed
    const collapseEl = this.element.closest('.collapse')
    if (collapseEl) {
      collapseEl.addEventListener('shown.bs.collapse', () => {
        if (!this.initialized && this.type_ids != "") {
          this.initializeAll()
        }
      })
      // Don't initialize if collapsed
      if (!collapseEl.classList.contains('show')) {
        return
      }
    }

    if (this.type_ids != "") {
      await this.initializeAll()
    }
  }

  newTypeIds = () => {
    const typeIdElement = document.getElementById('booking_type_ids')
    const typeIdInputs = document.querySelectorAll('input[name="booking[type_ids][]"]')

    if (typeIdElement) {
      this.type_ids = $(typeIdElement).val()
    } else if (typeIdInputs.length > 0) {
      this.type_ids = typeIdInputs[0].value
    }

    if (this.type_ids != "") {
       this.initializeAll()
    }
  }

  disconnect() {
    if (this.externalToggle && this._onExternalToggle) {
      this.externalToggle.removeEventListener('change', this._onExternalToggle)
    }
  }

  refreshForSiteChange() {
    if (!this.initialized) return
    this.fetchDisabledDatesForStartDateMonth()
    this.fetchStartTimeRange()
    if (this.hasEndDateTarget && this.endDateInstance) {
      this.fetchDisabledDatesForEndDateMonth()
      this.fetchEndTimeRange()
    }
  }

  async initializeAll() {
    if (this.initialized) return
    this.initialized = true

    this.partially_available_dates = await this.fetchPartiallyAvailableDatesForStartDateMonth()

    if (this.hasStartDateTarget) {
      const daysWithLowAvailability = this.partially_available_dates

      this.startDateInstance = this.initializeDatePicker(this.startDateTarget, {
        minDate: this.minDateValue,
        onDayCreate: function (dObj, dStr, fp, dayElem) {
          const date = String(dayElem.dateObj.toISOString().slice(0, 10))
          if (daysWithLowAvailability.includes(date)) {
            dayElem.classList.add('low-availability')
          }
        },
      })
      this.fetchDisabledDatesForStartDateMonth()
      this.selectedStartDate = this.startDateInstance.selectedDates[0]
      this.startTimeInstance = this.initializeTimePicker(this.startTimeTarget)
      this.fetchStartTimeRange()
    } else if(this.hasCurrentTimeTarget) {
      this.selectedStartDate = this.currentTimeTarget.innerHTML
    }
    if (this.hasEndDateTarget) {
      // Get initial value from the input before flatpickr overwrites it
      const initialEndDate = this.endDateTarget.value

      this.endDateInstance = this.initializeDatePicker(this.endDateTarget, {
        minDate: this.selectedStartDate,
        maxDate: this.maxDate,
        defaultDate: initialEndDate || this.selectedStartDate
      })
      this.selectedEndDate = this.endDateInstance.selectedDates[0]

      this.fetchDisabledDatesForEndDateMonth()
    }

    if (this.hasEndTimeTarget) {
      this.endTimeInstance = this.initializeTimePicker(this.endTimeTarget)
      this.fetchEndTimeRange()
    }
    this.setHandlers()

    if (this.overrideActive && this.startDateInstance) {
      this.startDateInstance.set('minDate', this.overrideMinDateValue)
    }
  }

  setHandlers() {
    if (this.startDateInstance) {
      this.startDateInstance.config.onMonthChange.push(this.fetchDisabledDatesForStartDateMonth)
      this.startDateInstance.config.onChange.push(this.updateStartTime)
    }

    if (this.endDateInstance) {
      this.endDateInstance.config.onMonthChange.push(this.fetchDisabledDatesForEndDateMonth)
      this.endDateInstance.config.onChange.push(this.updateEndTime)
    }
  }

  initializeDatePicker(element, options) {
    return flatpickr(element, {
      locale: German,
      altInput: true,
      enableTime: false,
      noCalendar: false,
      altFormat: 'D, j. F Y',
      dateFormat: 'Y-m-d',
      disableMobile: true,
      ...options,
    })
  }

  initializeTimePicker(element, values) {
    var tomselect = null;
    if (!element.tomselect) {
       tomselect = new TomSelect(element, {})
    } else {
       tomselect = element.tomselect
    }

    if (values) {
      tomselect.settings.maxOptions = values.length;
      const options = values.map((time) => ({
        value: time.toString(),
        text: `${time} Uhr`,
      }));

      var oldValue = tomselect.getValue()

      tomselect.clear();
      tomselect.clearOptions();
      tomselect.addOptions(options);

      if (values.includes(oldValue)) {
        tomselect.setValue(oldValue);
      } else {
        tomselect.setValue(values[0]);
      }
    }
  }

  setDisabledDates(data, instance) {
    instance.set('disable', data.unavailable_dates)
  }

  setStartTimeRange = (data, target) => {
    if (this.hasStartDateTarget && data.suggested_start_date && !this.editModeValue) {
      const suggestedDate = new Date(data.suggested_start_date)
      this.startDateInstance.setDate(suggestedDate)
      this.selectedStartDate = this.startDateInstance.selectedDates[0]

      if (this.hasStartTimeTarget && this.startTimeTarget && data.suggested_start_time) {
        if (this.startTimeTarget.tomselect) {
          this.startTimeTarget.tomselect.setValue(data.suggested_start_time)
        } else {
          this.startTimeTarget.value = data.suggested_start_time
        }
      }

      if (this.endDateInstance) {
        this.endDateInstance.set('minDate', this.selectedStartDate)
      }
    }

    let tmpStartDate = new Date(this.selectedStartDate)
    this.maxDate = new Date(tmpStartDate.setHours(
      tmpStartDate.getHours() + parseInt(data.max_duration || 24)
    ))

    this.max_duration = data.max_duration

    const values = data.business_hours
    if (this.hasStartTimeTarget) {
      this.startTimeInstance = this.initializeTimePicker(target, values)
    }

    if (this.endDateInstance) {
      if (this.editModeValue) {
        // In edit mode, just update maxDate but keep the current selection
        this.endDateInstance.set('maxDate', this.maxDate)
        return
      }

      // For new bookings, set the end date from suggestion
      if (data.end_date_suggestion) {
        let tmpEndDate = new Date(data.end_date_suggestion)
        if (!isNaN(tmpEndDate.getTime())) {
          // Ensure end date doesn't exceed maxDate
          if (tmpEndDate > this.maxDate) {
            tmpEndDate = this.maxDate
          }
          // Set maxDate first, then the date
          this.endDateInstance.set('maxDate', this.maxDate)
          this.endDateInstance.setDate(tmpEndDate)
          this.selectedEndDate = this.endDateInstance.selectedDates[0]
          this.fetchEndTimeRange()
        }
      } else {
        this.endDateInstance.set('maxDate', this.maxDate)
      }
    }
  }

  setEndTimeRange(data, target) {
    const values = data.business_hours

    this.endTimeInstance = this.initializeTimePicker(target, values)
  }

  updateStartTime = () => {
    if (!this.startDateInstance) return
    this.selectedStartDate = this.startDateInstance.selectedDates[0]
    this.fetchStartTimeRange()
    if (this.endDateInstance) {
      this.endDateInstance.set('minDate', this.selectedStartDate)
      this.selectedEndDate = this.endDateInstance.selectedDates[0]
      this.fetchEndTimeRange()
    }
  }

  updateEndTime = () => {
    if (!this.endDateInstance) return
    this.selectedEndDate = this.endDateInstance.selectedDates[0]
    this.fetchEndTimeRange()
  }

  fetchStartTimeRange() {
    const siteId = this.getSiteId()
    let url =
      '/start_time/?start_date=' +
      this.selectedStartDate +
      '&type_id=' +
      this.type_ids
    if (siteId) {
      url += '&site_id=' + siteId
    }
    if (this.siteChanged) {
      url += '&reset=true'
      this.siteChanged = false
    }
    url += this.overrideParam

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if(this.hasStartTimeTarget) {
          this.setStartTimeRange(data, this.startTimeTarget)
        }
      })
  }

  fetchEndTimeRange() {
    if (this.selectedEndDate != null) {
      const siteId = this.getSiteId()
      let url = '/end_time/?end_date=' + this.selectedEndDate + '&type_id=' + this.type_ids
      if (siteId) {
        url += '&site_id=' + siteId
      }
      url += this.overrideParam

      fetch(url)
        .then((response) => response.json())
        .then((data) => this.setEndTimeRange(data, this.endTimeTarget))
    }
  }

  fetchDisabledDatesForStartDateMonth = () => {
    const siteId = this.getSiteId()
    let url = '/unavailable_dates/?current_month=' + (this.startDateInstance.currentMonth + 1) +
      '&current_year=' + this.startDateInstance.currentYear +
      '&type_id=' + this.type_ids
    if (siteId) {
      url += '&site_id=' + siteId
    }
    url += this.overrideParam

    fetch(url)
      .then((response) => response.json())
      .then((data) => this.setDisabledDates(data, this.startDateInstance))
  }

  fetchPartiallyAvailableDatesForStartDateMonth = () => {
    const siteId = this.getSiteId()
    const now = new Date()
    let url = '/unavailable_dates?current_month=' + (now.getMonth() + 1) +
      '&current_year=' + now.getFullYear() +
      '&type_id=' + this.type_ids
    if (siteId) {
      url += '&site_id=' + siteId
    }

    return fetch(url)
      .then((response) => response.json())
      .then((data) => {return data.partially_available_dates})
  }

  fetchDisabledDatesForEndDateMonth = () => {
    const siteId = this.getSiteId()
    let url = '/unavailable_dates/?current_month=' + (this.endDateInstance.currentMonth + 1) +
      '&current_year=' + this.endDateInstance.currentYear +
      '&type_id=' + this.type_ids
    if (siteId) {
      url += '&site_id=' + siteId
    }
    url += this.overrideParam

    fetch(url)
      .then((response) => response.json())
      .then((data) => this.setDisabledDates(data, this.endDateInstance))
  }

  get overrideActive() {
    if (this.hasOverrideToggleTarget) {
      return this.adminCanOverrideValue && this.overrideToggleTarget.checked
    }
    if (this.externalToggle) {
      return this.adminCanOverrideValue && this.externalToggle.checked
    }
    return this.adminCanOverrideValue && this.overrideActiveValue
  }

  get overrideParam() {
    return this.overrideActive ? '&override=true' : ''
  }

  toggleOverride() {
    if (!this.adminCanOverrideValue) return

    if (this.startDateInstance) {
      const newMinDate = this.overrideActive ? this.overrideMinDateValue : this.minDateValue
      this.startDateInstance.set('minDate', newMinDate)
      this.fetchDisabledDatesForStartDateMonth()
      this.fetchStartTimeRange()
    }
    if (this.endDateInstance) {
      this.fetchDisabledDatesForEndDateMonth()
      this.fetchEndTimeRange()
    }
  }

  getSiteId() {
    const siteRadio = document.querySelector('input[type="radio"][name*="site_id"]:checked')
    return siteRadio ? siteRadio.value : null
  }

  toggle(event) {
    event.target.closest('.input-group').querySelector('.form-control')._flatpickr.toggle()
  }
}
